/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-empty */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import Uppy from '@uppy/core';
import { useUppy, Dashboard } from '@uppy/react';
import XHRUpload from '@uppy/xhr-upload';
import debounce from 'lodash/debounce';
import AwsS3 from '@uppy/aws-s3';
import Webcam from '@uppy/webcam';
import { Input, Icon } from 'rsuite';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Textarea,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Flex,
  Box,
  Text,
  Heading,
  Divider,
} from '@chakra-ui/react';
import { User } from '../../../@types';
import { ModalWrapper } from '../styles/ModalWrapper';
import * as Constants from '../../../constants';
import { showNotification } from '../../notification';
import { VideoResponseWrapper } from '../../episode-video/components/QuestionResponseView';
import { ResponseModalFooter } from './ResponseModalFooter';
import {
  useSubmitResponse,
  useCurrentUser,
  useUserPromptResponses,
  getSignedUrl,
} from '../../../api';

const ExistingWrapper = styled.div`
  height: 300px;
  display: flex;
  .video-container {
    width: 80%;
    margin: 0 auto;
    direction: ltr;
    .video-js {
      height: 100%;
      padding: 0;
    }
    video {
      width: 100%;
      object-fit: contain;
      border-radius: 0;
    }
  }
`;

export const VideoResponseModal: React.FC<{
  user: User;
  promptId: string;
  show: boolean;
  questionText: string;
  icon: React.ReactNode;
  closeModal: () => void;
  setShowVideoResponseModal: (show: boolean) => void;
}> = ({
  user,
  promptId,
  show,
  setShowVideoResponseModal,
  questionText,
  icon,
  closeModal,
}) => {
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [fileAdded, setFileAdded] = useState(false);

  const {
    data: responses,
    isLoading: responsesLoading,
  } = useUserPromptResponses(promptId, user.id);

  const myResponse = responses && responses[Constants.RESPONSE_TYPE_IDS.VIDEO];

  const {
    mutateAsync: submitResponse,
    isLoading,
    isError,
    error: submitErrorMessage,
  } = useSubmitResponse(promptId);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const shouldSaveDescription = useRef(!myResponse);

  const [responseText, setResponseText] = useState(myResponse?.text || '');

  const handleInputText = (text: string) => {
    setResponseText(text);
  };

  const handleSubmitResponse = useCallback(
    async ({ shouldShowNotification = true, ...rest }) => {
      try {
        const res = await submitResponse({
          prompt_id: promptId,
          response_type: Constants.RESPONSE_TYPE_IDS.VIDEO,
          text: responseText,
          ...rest,
        });
        if (shouldShowNotification) {
          if (res.success) {
            showNotification({
              type: 'success',
              title: 'Success!',
              message: 'Your video response was submitted!',
            });
          } else {
            showNotification({
              type: 'error',
              title: 'Oh no!',
              message:
                'There was a problem uploading your video. Please try again.',
            });
          }
        }
        return res;
      } catch (e) {
        //
      }
      return false;
    },
    [promptId, Constants, showNotification, submitResponse, responseText]
  );

  const handleClose = () => {
    setShowVideoResponseModal(false);
    onClose();
  };

  useEffect(() => {
    const saveDescription = async () => {
      await handleSubmitResponse({
        shouldShowNotification: false,
      });
    };
    if (uploadSuccess && !!shouldSaveDescription.current) {
      shouldSaveDescription.current = false;
      saveDescription();
    }
  }, [uploadSuccess]);

  const uppyWebcam = useUppy(() => {
    return new (Uppy as any)({
      id: 'uppy-video',
      autoProceed: false,
      restrictions: {
        maxNumberOfFiles: 1,
        minNumberOfFiles: 1,
        allowedFileTypes: ['video/*'],
      },
      meta: {
        user_id: user.id,
        response_type: Constants.RESPONSE_TYPE_IDS.VIDEO.toString(),
      },
      onBeforeFileAdded: (currentFile: any, files: any) => {
        setFileAdded(true);
      },
    })
      .use(Webcam, {
        modes: ['video-audio'],
        preferredVideoMimeType: 'video/webm',
      })
      .use(AwsS3, {
        async getUploadParameters(file: any) {
          const {
            url,
            base_path,
            full_path,
            base_name,
            full_name,
          } = await getSignedUrl(promptId, file.extension);

          uppyWebcam.setMeta({
            base_path,
            full_path,
            base_name,
            full_name,
          });
          return {
            url,
            method: 'PUT',
            headers: {
              contentType: file.type,
            },
          };
        },
      })
      .on('upload-success', async (file: any, response: any) => {
        setFileAdded(false);
        const { base_path, full_path, base_name, full_name } = file.meta;
        const submit = await handleSubmitResponse({
          base_path,
          full_path,
          base_name,
          full_name,
          shouldCloseModal: false,
        });
        setUploadSuccess(submit.success);
      });
  });

  useEffect(() => {
    if (show) onOpen();
  }, [show]);

  if (!user || responsesLoading) return null;

  const { message: errorMessage = '' } = (submitErrorMessage as any) || {};
  const strippedText = questionText.replace(/<[^>]+>/g, '');

  return (
    <Modal size="3xl" isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalWrapper>
        <ModalContent className="video-response">
          <ModalHeader>{icon} Respond with a video</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl isInvalid={isError}>
              <FormLabel>
                <div
                  className="question-text"
                  dangerouslySetInnerHTML={{ __html: strippedText }}
                />
              </FormLabel>
              <Flex textAlign="center" align="center">
                <Box w="100%">
                  <Heading
                    my={4}
                    color="gray.500"
                    fontWeight="semibold"
                    fontSize="md"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Icon size="2x" icon="import" />
                    <Box ml={2} as="span">
                      {!myResponse
                        ? 'Record or Upload a Video'
                        : 'Your Video Response'}
                    </Box>
                  </Heading>
                  {uppyWebcam && !myResponse && (
                    <Dashboard
                      uppy={uppyWebcam}
                      proudlyDisplayPoweredByUppy={false}
                      plugins={['Webcam']}
                    />
                  )}
                  {!!myResponse && (
                    <ExistingWrapper>
                      <VideoResponseWrapper response={myResponse} isEditView />
                    </ExistingWrapper>
                  )}
                </Box>
              </Flex>
              <Box mt={8} px={14}>
                <FormLabel fontSize="sm">Description:</FormLabel>
                <Textarea
                  placeholder="Enter a description of your video... (optional)"
                  size="md"
                  onChange={(e) => handleInputText(e.target.value)}
                  value={responseText}
                />
                <FormErrorMessage>{`${errorMessage}`}</FormErrorMessage>
              </Box>
            </FormControl>
          </ModalBody>

          <ResponseModalFooter
            myResponse={myResponse}
            handleClose={handleClose}
            fileAdded={fileAdded}
            isLoading={isLoading}
            isDisabled={!myResponse && !uploadSuccess}
            onSubmit={handleSubmitResponse}
            onDelete={() => null}
          />
        </ModalContent>
      </ModalWrapper>
    </Modal>
  );
};
